import { useState, useEffect } from 'react';
import { useUpdateProgramContacts } from '../../../hooks/useProgramContacts';
import styles from './ContactsPanel.module.css';

export interface QcFlagCardProps {
  programId: string;
  qcFlag: boolean;
  qcNotes: string | null;
}

export function QcFlagCard({ programId, qcFlag, qcNotes }: QcFlagCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedNotes, setEditedNotes] = useState(qcNotes || '');
  const [editedFlag, setEditedFlag] = useState(qcFlag);

  const updateMutation = useUpdateProgramContacts({
    onSuccess: () => {
      setIsEditing(false);
    }
  });

  // Reset when props change (e.g., after QC run)
  useEffect(() => {
    setEditedNotes(qcNotes || '');
    setEditedFlag(qcFlag);
    setIsEditing(false);
  }, [qcFlag, qcNotes]);

  const isDirty = editedFlag !== qcFlag || editedNotes !== (qcNotes || '');

  const handleSave = () => {
    updateMutation.mutate({
      program_id: programId,
      qc_flag_contacts: editedFlag,
      qc_flag_contacts_notes: editedNotes || null
    });
  };

  const handleCancel = () => {
    setEditedNotes(qcNotes || '');
    setEditedFlag(qcFlag);
    setIsEditing(false);
  };

  const flagColor = editedFlag ? '#ef4444' : '#10b981';
  const flagBgColor = editedFlag ? '#fef2f2' : '#f0fdf4';
  const flagText = editedFlag ? 'Flagged for Review' : 'Verified';

  return (
    <div className={styles.card}>
      <h2 className={styles.cardHeader}>QC Status</h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* QC Flag Status */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <span
              style={{
                display: 'inline-block',
                width: '0.75rem',
                height: '0.75rem',
                borderRadius: '50%',
                backgroundColor: flagColor
              }}
            />
            <span style={{ fontSize: '0.875rem', fontWeight: '500', color: flagColor }}>
              {flagText}
            </span>
          </div>

          {isEditing ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                <input
                  type="checkbox"
                  checked={editedFlag}
                  onChange={(e) => setEditedFlag(e.target.checked)}
                  style={{ width: '1rem', height: '1rem' }}
                />
                Flag for manual review
              </label>
            </div>
          ) : (
            <div
              style={{
                padding: '0.75rem',
                backgroundColor: flagBgColor,
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                color: '#374151'
              }}
            >
              {qcFlag
                ? 'This record requires manual review'
                : 'Contact information verified'}
            </div>
          )}
        </div>

        {/* QC Notes */}
        <div>
          <label htmlFor="qc_notes" style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
            QC Review Notes
          </label>

          {isEditing ? (
            <textarea
              id="qc_notes"
              value={editedNotes}
              onChange={(e) => setEditedNotes(e.target.value)}
              rows={6}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
              placeholder="Add notes about why this was flagged or what needs review..."
            />
          ) : (
            <div
              style={{
                minHeight: '6rem',
                padding: '0.75rem',
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
                color: qcNotes ? '#374151' : '#9ca3af'
              }}
            >
              {qcNotes || 'No review notes'}
            </div>
          )}
        </div>

        {/* Edit/Save buttons */}
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#ffffff',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Edit QC Status
            </button>
          ) : (
            <>
              <button
                onClick={handleSave}
                disabled={!isDirty || updateMutation.isPending}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: isDirty ? 'var(--color-brand, #1c8e1c)' : '#d1d5db',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: isDirty ? 'pointer' : 'not-allowed',
                  opacity: isDirty ? 1 : 0.6
                }}
              >
                {updateMutation.isPending ? 'Saving...' : 'Save QC Status'}
              </button>

              <button
                onClick={handleCancel}
                disabled={updateMutation.isPending}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </>
          )}
        </div>

        {updateMutation.isError && (
          <div className={styles.error}>
            Error updating QC status: {updateMutation.error.message}
          </div>
        )}

        {updateMutation.isSuccess && (
          <div style={{ padding: '0.75rem', backgroundColor: '#ecfdf5', borderRadius: '0.375rem', fontSize: '0.875rem', color: '#059669' }}>
            QC status updated successfully!
          </div>
        )}
      </div>
    </div>
  );
}

export default QcFlagCard;
